import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchApi } from './fetchApi';


import ArtikelThumbnail from './ArtikelThumbnail';
const ArtikelThumbnailAny = ArtikelThumbnail as any;


interface LieferscheinPosition {
  id: number;
  artikel_id: number;
  menge: number;
  sku: string;
  bezeichnung: string;

  regalnummer?: string | null;
  fachnummer?: string | null;
  boxnummer?: string | null;
  bild_url?: string | null;

}

interface LieferscheinDaten {
  id: number;
  lieferschein_nr: string;
  quelle_typ: string;
  quelle_id: number | null;
  ziel_typ: string;
  ziel_id: number | null;
  bemerkung: string | null;
  erstellt_am: string;
  empfaenger: string;
  quelle_name: string;
  status: string;
  positionen: LieferscheinPosition[];
}

export default function LieferscheinDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [daten, setDaten] = useState<LieferscheinDaten | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    async function ladeDetails() {
      if (!id) {
        setFehler('Keine Lieferschein-ID in der URL gefunden.');
        setLaden(false);
        return;
      }

      try {
        const res = await fetchApi(
  `/api/lieferschein/view/${id}`
);

        if (!res.ok) throw new Error(`Serverantwort: ${res.status}`);

        const json = await res.json();

        if (!json || !json.id) {
          setFehler('Keine Daten gefunden.');
        } else {
          setDaten(json);
        }
      } catch (err: any) {
  console.error('❌ Fehler beim Laden des Lieferscheins:', err);
  setFehler(
    err?.message === '401'
      ? 'Session abgelaufen. Bitte neu einloggen.'
      : 'Fehler beim Laden des Lieferscheins.'
  );
} finally {
  setLaden(false);
}

    }

    ladeDetails();
  }, [id]);

  if (laden) {
    return (
      <Container>
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (fehler) {
    return (
      <Container>
        <Box mt={4} mb={2}>
          <Alert severity="error">{fehler}</Alert>
        </Box>
        <Button
          variant="contained"
          onClick={() => navigate('/lieferschein-liste')}
        >
          Zurück zur Übersicht
        </Button>
      </Container>
    );
  }

  if (!daten) {
    return (
      <Container>
        <Box mt={4} mb={2}>
          <Alert severity="error">Keine Daten gefunden.</Alert>
        </Box>
        <Button
          variant="contained"
          onClick={() => navigate('/lieferschein-liste')}
        >
          Zurück zur Übersicht
        </Button>
      </Container>
    );
  }

  const darfBearbeiten = daten.status === 'offen';
  const istWarenausgang = daten.quelle_typ === 'LAGERORT';
  const istWareneingang = daten.quelle_typ === 'LIEFERANT' || daten.quelle_typ === 'UNERWARTET';
  const istInterneUmlagerung = daten.quelle_typ === 'LAGERORT' && daten.ziel_typ === 'LAGERORT';

  return (
    <Container>
      {/* Kopfzeile + Buttons */}
      <Box
        mt={4}
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5">
          Lieferschein {daten.lieferschein_nr}
        </Typography>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/lieferschein-liste')}
          >
            Zurück zur Übersicht
          </Button>

          {darfBearbeiten && (
            <Button
              variant="contained"
              onClick={() => navigate(`/lieferschein/edit/${daten.id}`)}
            >
              Bearbeiten
            </Button>
          )}

          {/* Packzettel nur bei Warenausgang (LAGERORT → …) und offen */}
          {darfBearbeiten && istWarenausgang && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(`/lieferschein/packzettel/${daten.id}`)}
            >
              Packzettel
            </Button>
          )}

          {/* Packen nur bei Warenausgang (LAGERORT → …) und offen */}
          {darfBearbeiten && istWarenausgang && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(`/lieferschein/packen/${daten.id}`)}
            >
              Packen
            </Button>
          )}

          {/* Einlagern bei Wareneingang (LIEFERANT → Lagerort) wenn offen */}
          {darfBearbeiten && istWareneingang && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(`/lieferschein/einlagern/${daten.id}`)}
            >
              Einlagern
            </Button>
          )}

          {/* Einlagern bei interner Umlagerung (LAGERORT → LAGERORT) wenn versendet */}
          {daten.status === 'versendet' && istInterneUmlagerung && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => navigate(`/lieferschein/einlagern/${daten.id}`)}
            >
              Einlagern
            </Button>
          )}
        </Box>
      </Box>

      {/* Hinweis bei versendet – je nach Typ unterschiedliche Meldung */}
      {daten.status === 'versendet' && !istInterneUmlagerung && (
        <Box mb={2}>
          <Alert severity="info">
            Dieser Lieferschein wurde bereits versendet und kann nicht mehr
            geändert werden.
          </Alert>
        </Box>
      )}
      {daten.status === 'versendet' && istInterneUmlagerung && (
        <Box mb={2}>
          <Alert severity="warning">
            Ware wurde aus dem Quelllagerort entnommen. Bitte jetzt im Ziellagerort einlagern.
          </Alert>
        </Box>
      )}
      {daten.status === 'abgeschlossen' && (
        <Box mb={2}>
          <Alert severity="success">
            Interne Umlagerung abgeschlossen.
          </Alert>
        </Box>
      )}

      {/* Kopfbereich */}
      <Box mb={3}>
        <Typography variant="subtitle1">
          Sender: <strong>{daten.quelle_name}</strong>
        </Typography>

        <Typography variant="subtitle1">
          Empfänger: <strong>{daten.empfaenger}</strong>
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1 }}>
          Status{' '}
          {daten.status === 'offen' ? (
            <strong style={{ color: 'orange' }}>offen</strong>
          ) : daten.status === 'versendet' ? (
            <strong style={{ color: 'green' }}>versendet</strong>
          ) : (
            <strong style={{ color: '#1976d2' }}>abgeschlossen</strong>
          )}
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Erstellt am:{' '}
          {new Date(daten.erstellt_am).toLocaleString('de-AT')}
        </Typography>

        {daten.bemerkung && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Bemerkung: {daten.bemerkung}
          </Typography>
        )}
      </Box>

      {/* Positionen-Tabelle */}
      <Typography variant="h6" gutterBottom>
        Positionen
      </Typography>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>SKU</TableCell>
            <TableCell>Artikel</TableCell>
            <TableCell>Regal</TableCell>
            <TableCell>Fach</TableCell>
            <TableCell>Box</TableCell>
            <TableCell align="right">Menge</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {daten.positionen.map((pos) => (
            <TableRow key={pos.id}>
              <TableCell>
  <Box display="flex" alignItems="center" gap={1}>
    <ArtikelThumbnailAny
      src={pos.bild_url || null}
      url={pos.bild_url || null}
      alt={pos.sku}
      size={28}
    />
    <span>{pos.sku}</span>
  </Box>
</TableCell>

              <TableCell>{pos.bezeichnung}</TableCell>
              <TableCell>{pos.regalnummer || '-'}</TableCell>
              <TableCell>{pos.fachnummer || '-'}</TableCell>
              <TableCell>{pos.boxnummer || '-'}</TableCell>
              <TableCell align="right">{pos.menge}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Container>
  );
}
